import React, { useCallback, useState, useEffect, useMemo, useContext } from 'react';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	handleKeyboardShortcutNavigation,
	ScrollBehavior,
	NavigationContext,
	ShortcutType,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';
import {
	getNextUnreadThreadKey,
	scrollCommentIntoView,
} from '@confluence/comments-util/entry-points/domUtils';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
	AddFooterCommentMode,
} from '@confluence/comment-context/entry-points/ObjectCommentContext';
import { useInlineCommentsState } from '@confluence/inline-comments-hooks/entry-points/useInlineComments';
import {
	useInlineCommentsContext,
	useInlineCommentsDispatchContext,
} from '@confluence/comment-context/entry-points/InlineCommentsContext';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import {
	selectInlineComment,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/comments-data-manager/entry-points/getCommentsDataManagerExperimentAttributes';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { withDialogs } from '@confluence/dialogs/entry-points/withDialogs';
import { type DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener';
import {
	NEXT_COMMENT_SHORTCUT,
	PREV_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_UNREAD_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_COMMENT_SHORTCUT,
	UNIVERSAL_PREV_COMMENT_SHORTCUT,
	CREATE_COMMENT_SHORTCUT,
	UNIVERSAL_OPEN_COMMENT_EDITOR,
	UNIVERSAL_SELECT_COMMENT_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import { handlePanelSelectionAndScroll } from '@confluence/comments-panel/entry-points/commentThreadHelper';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	useCommentsPanel,
	useCommentsPanelState,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useObjectSidebarState } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName';
import { fg } from '@confluence/feature-gating';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useCreateCommentAction } from '@confluence/highlight-actions/entry-points/useCreateCommentAction';
import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery';
import {
	useCommentsDataState,
	CommentType,
	type CommentsDataMap as LegacyCommentsDataMap,
	FooterCommentsLoadingState,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { CommentsType } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import { expVal } from '@confluence/feature-experiments';

import {
	getNextRef,
	parsePanelAndInlineRefs,
	isNavigatingToPotentiallyUnloadedFooterComment,
} from './helper/commentsShortcutsHelper';
import { useFooterComments } from './useFooterComments';

type ViewCommentShortcutListenerProps = {
	isFabricPage: boolean;
	contentId?: string;
	dialogs: DialogsStateContainer;
};

const ViewCommentShortcutListenerComponent = ({
	isFabricPage,
	contentId,
	dialogs,
}: ViewCommentShortcutListenerProps) => {
	const { navigateToInlineComment } = useInlineCommentsDispatchContext();
	const { activeHighlight } = useInlineCommentsContext();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { removeCommentQueryParams } = useInlineCommentQueryParams();
	const { unresolvedInlineComments } = useInlineCommentsState();
	const pageMode = useGetPageMode();
	const {
		orderedActiveAnnotationIdList,
		commentsDataMap: legacyCommentsDataMap,
		footerCommentsLoadingState,
	} = useCommentsDataState();

	// New comments data manager hooks - only used when experiment is enabled
	const newCommentsDataResult = useAllCommentsData({
		contentId: contentId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(pageMode),
		options: {
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !contentId,
		},
	});

	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? (newCommentsDataResult.commentsDataMap as LegacyCommentsDataMap)
		: legacyCommentsDataMap;

	const emitter = getRendererAnnotationEventEmitter();
	const pageClass = usePageClass();
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isNextCommentLoading, setIsNextCommentLoading] = useState(false);
	const [alreadyFetchedInitialData, setAlreadyFetchedInitialData] = useState(false);
	const [triggeredByUnreadKeyboardShortcut, setTriggeredByUnreadKeyboardShortcut] = useState(false);

	// Used to facilitate loading footer comments and navigating after load
	// Should only be set when confluence_frontend_kb_footer_loading is true
	const [pendingStandaloneAction, setPendingStandaloneAction] = useState<
		('next' | 'previous') | null
	>(null);

	const [
		{ currentlyRenderedThreads, currentlySelectedThreadKey },
		{ setCurrentlySelectedThreadKey },
	] = useCommentsPanel();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const { isObjectSidebarShown, panel } = useObjectSidebarState();
	const { expandCommentContainer, setFooterEditorEntry } = useObjectCommentDispatchContext();
	const { expanded, isObjectCommentWrapperVisible, footerEditorEntry } = useObjectCommentContext();
	const { addFooterCommentAdf } = useCommentsPanelState();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { unreadCommentIDSet } = useUnreadCommentsState();
	const { canAddComments } = useAddCommentPermissionCheck(contentId ?? '');

	const orderedActiveAnnotations = useMemo(() => {
		return fg('confluence_frontend_new_comments_scrolling')
			? orderedActiveAnnotationIdList.map((a) => a.threadKey)
			: unresolvedInlineComments;
	}, [orderedActiveAnnotationIdList, unresolvedInlineComments]);

	useEffect(() => {
		setIsNextCommentLoading(false);
	}, [activeHighlight, currentlySelectedThreadKey]);

	useEffect(() => {
		return () => {
			setCurrentlySelectedThreadKey('');
		};
	}, [setCurrentlySelectedThreadKey]);

	const { footerCommentThreads, footerCommentIds } = useFooterComments({ contentId });

	const { unreadInlineComments, unreadGeneralComments } = useMemo(() => {
		const unreadInlineComments: string[] = [];
		const unreadGeneralComments: string[] = [];

		orderedActiveAnnotations.forEach((threadKey) => {
			const commentData = commentsDataMap.inline[threadKey];
			if (commentData) {
				let isCommentUnread = false;
				let hasUnreadReplies = false;

				if (fg('confluence_frontend_unread_comments_fixes')) {
					// Use unreadCommentIDSet when feature flag is enabled
					// in SSR, unreadCommentIDSet is undefined
					isCommentUnread = unreadCommentIDSet?.has(commentData.id);
					hasUnreadReplies =
						commentData.replies?.some((reply) => unreadCommentIDSet?.has(reply.id)) || false;
				} else {
					// Use legacy .isUnread field when feature flag is disabled
					isCommentUnread = commentData.isUnread || false;
					hasUnreadReplies = commentData.replies?.some((reply) => reply.isUnread) || false;
				}

				if (isCommentUnread || hasUnreadReplies) {
					unreadInlineComments.push(threadKey);
				}
			}
		});
		Object.keys(commentsDataMap.general).forEach((threadKey) => {
			const commentData = commentsDataMap.general[threadKey];
			if (commentData) {
				let isCommentUnread = false;
				let hasUnreadReplies = false;

				if (fg('confluence_frontend_unread_comments_fixes')) {
					// Use unreadCommentIDSet when feature flag is enabled
					isCommentUnread = unreadCommentIDSet?.has(commentData.id);
					hasUnreadReplies =
						commentData.replies?.some((reply) => unreadCommentIDSet?.has(reply.id)) || false;
				} else {
					// Use legacy .isUnread field when feature flag is disabled
					isCommentUnread = commentData.isUnread || false;
					hasUnreadReplies = commentData.replies?.some((reply) => reply.isUnread) || false;
				}

				if (isCommentUnread || hasUnreadReplies) {
					unreadGeneralComments.push(threadKey);
				}
			}
		});

		return {
			unreadInlineComments,
			unreadGeneralComments,
		};
	}, [commentsDataMap, orderedActiveAnnotations, unreadCommentIDSet]);

	const sendKeyboardShortcutAnalytics = (shortcut: string) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'viewPage',
				action: 'pressed',
				actionSubject: 'keyboardShortcut',
				actionSubjectId: 'createComment',
				attributes: {
					key: shortcut,
				},
			},
		}).fire();
	};

	const { handleCommentButtonClick } = useCreateCommentAction({
		contentId: contentId || '',
		isFabric: isFabricPage,
		sendKeyboardShortcutAnalytics,
	});

	const getCommentIndex = useCallback(
		(currentThreadKey: string): number => {
			return fg('confluence_frontend_content_wrapper')
				? [...orderedActiveAnnotations, ...footerCommentIds]?.findIndex(
						(threadKey) => threadKey === currentThreadKey,
					)
				: orderedActiveAnnotations?.findIndex((threadKey) => threadKey === currentThreadKey);
		},
		[orderedActiveAnnotations, footerCommentIds],
	);

	const openInlineComment = useCallback(
		(markerRef: string) => {
			if (
				(pageClass === 'renderer' || pageClass === 'editor') &&
				fg('confluence_frontend_view_inline_comment_keyboard')
			) {
				experienceTracker.start({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
					attributes: {
						...getCommentsDataManagerExperimentAttributes(),
						annotationId: markerRef,
						entryPoint: 'comments-shortcuts/ViewCommentShortcutListener',
						pageClass: 'renderer',
					},
				});
			}
			setCurrentlySelectedThreadKey('');
			const commentElement = document.getElementById(markerRef);
			selectInlineCommentCompoundExperience.addCommonAttributes({
				annotationId: markerRef,
			});
			selectInlineComment.debug(
				`Keyboard selection now triggering annotation selection for annotation ${markerRef}`,
			);
			emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
				annotationIds: [markerRef],
				eventTarget: commentElement,
			});
			if (fg('confluence_comments_unified_navigation')) {
				handleKeyboardShortcutNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					block: 'center',
					annotationId: markerRef,
					commentType: CommentsType.INLINE,
					isCommentsPanelOpen: isObjectSidebarShown && fg('confluence-frontend-comments-panel'),
					hasKeyPressed: true,
				});
			} else {
				scrollCommentIntoView({ commentElement, isFabricPage: true });
			}
		},
		[emitter, setCurrentlySelectedThreadKey, experienceTracker, pageClass, isObjectSidebarShown],
	);

	const scrollToFooterComment = useCallback(
		(threadKey: string) => {
			setCurrentlySelectedThreadKey(threadKey);
			emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
				annotationIds: [],
				eventTarget: null,
			});
			if (fg('confluence_comments_unified_navigation')) {
				handleKeyboardShortcutNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					block: 'center',
					annotationId: threadKey,
					commentType: CommentsType.FOOTER,
					isCommentsPanelOpen: isObjectSidebarShown && fg('confluence-frontend-comments-panel'),
					hasKeyPressed: true,
				});
			} else {
				handlePanelSelectionAndScroll({
					threadKey,
					isResolved: false,
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: emitter,
					commentType: CommentType.GENERAL,
				});
			}
		},
		[emitter, pageMode, setCurrentlySelectedThreadKey, isObjectSidebarShown],
	);

	const onNavigationClick = useCallback(
		(nextMarkerRef: string) => {
			if (hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						openInlineComment(nextMarkerRef);
					},
				});
			} else {
				openInlineComment(nextMarkerRef);
			}
		},
		[hasContentChanged, resetContentChanged, showModal, openInlineComment],
	);

	const handlePanelCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			// If nothing is on the panel, there's nothing to navigate to
			if (currentlyRenderedThreads.length === 0) {
				return;
			}

			const { panelCommentRefs, navigableInlineCommentRefs } = parsePanelAndInlineRefs(
				currentlyRenderedThreads,
				orderedActiveAnnotations,
			);

			const nextRef = getNextRef(action, panelCommentRefs, currentlySelectedThreadKey);

			// Use unified keyboard shortcut navigation for panel navigation when feature is enabled
			if (fg('confluence_comments_unified_navigation')) {
				// Handle panel selection state management in the listener
				setCurrentlySelectedThreadKey(nextRef.ref);

				handleKeyboardShortcutNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					block: 'center',
					annotationId: nextRef.ref,
					commentType:
						nextRef.type === CommentType.GENERAL ? CommentsType.FOOTER : CommentsType.INLINE,
					isCommentsPanelOpen: isObjectSidebarShown && fg('confluence-frontend-comments-panel'),
					hasKeyPressed: true,
					context: NavigationContext.RENDERER,
					isResolved: !navigableInlineCommentRefs.has(nextRef.ref),
				});
			} else {
				// Use old method when unified navigation is disabled
				handlePanelSelectionAndScroll({
					threadKey: nextRef.ref,
					isResolved: !navigableInlineCommentRefs.has(nextRef.ref),
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: emitter,
					commentType: nextRef.type,
					scrollCommentsPanelToThread,
					shouldScrollCommentsPanel: true,
					hasKeyPressed: true,
				});
			}
		},
		[
			currentlyRenderedThreads,
			orderedActiveAnnotations,
			currentlySelectedThreadKey,
			pageMode,
			setCurrentlySelectedThreadKey,
			emitter,
			scrollCommentsPanelToThread,
			isObjectSidebarShown,
		],
	);

	const handleNextCommentNavigation = useCallback(
		({
			action,
			isNavigateToNextUnread,
		}: {
			isNavigateToNextUnread: boolean;
			action: 'next' | 'previous';
		}) => {
			//Check if the next inline comment is still in process of being rendered. Otherwise, quickly pressing keys will force comment to open and close instead of navigating
			if (isNextCommentLoading) return;

			setIsNextCommentLoading(true);

			navigateToInlineComment({
				action,
				triggeredByKeyboardShortcut: true,
				currentCommentIndex: getCommentIndex(activeHighlight || currentlySelectedThreadKey || ''),
				getCommentIndex,
				onNavigationClick,
				isEditor: false,
				removeCommentQueryParams,
				unresolvedInlineComments: orderedActiveAnnotations,
				createAnalyticsEvent,
				contentId,
				isNavigateToNextUnread,
				unreadInlineComments,
				unreadGeneralComments,
				footerComments: footerCommentIds,
				scrollToFooterComment,
				commentsDataMap,
			});
		},
		[
			isNextCommentLoading,
			navigateToInlineComment,
			getCommentIndex,
			activeHighlight,
			currentlySelectedThreadKey,
			onNavigationClick,
			removeCommentQueryParams,
			orderedActiveAnnotations,
			createAnalyticsEvent,
			contentId,
			unreadInlineComments,
			unreadGeneralComments,
			footerCommentIds,
			scrollToFooterComment,
			commentsDataMap,
		],
	);

	const handleStandaloneCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			if (activeHighlight || currentlySelectedThreadKey) {
				// Check if we're navigating to a potentially unloaded footer comment
				const currentIndex = getCommentIndex(activeHighlight || currentlySelectedThreadKey || '');
				const totalComments = [...orderedActiveAnnotations, ...footerCommentIds].length;
				const navigatingToPotentiallyUnloadedFooterComment =
					isNavigatingToPotentiallyUnloadedFooterComment(
						action,
						currentIndex,
						totalComments,
						footerCommentsLoadingState,
					);

				if (
					navigatingToPotentiallyUnloadedFooterComment &&
					fg('confluence_frontend_kb_footer_loading')
				) {
					setPendingStandaloneAction(action);
				} else {
					handleNextCommentNavigation({
						isNavigateToNextUnread: false,
						action,
					});
				}
			} else {
				// No highlight selected, so we navigate to the first inline comment
				const firstInlineCommentMarkerRef = orderedActiveAnnotations[0];

				if (
					!firstInlineCommentMarkerRef &&
					(fg('confluence_frontend_content_wrapper') ? footerCommentThreads.length === 0 : true)
				) {
					// No active inline comments or footer comments found, but footer is not yet loaded - load footer comments and try again
					if (
						footerCommentsLoadingState === FooterCommentsLoadingState.NOT_LOADED &&
						fg('confluence_frontend_kb_footer_loading')
					) {
						setPendingStandaloneAction(action);
					}

					return;
				}

				if (firstInlineCommentMarkerRef) {
					openInlineComment(firstInlineCommentMarkerRef);
				} else if (fg('confluence_frontend_content_wrapper')) {
					scrollToFooterComment(footerCommentThreads[0]?.id);
				}
			}
		},
		[
			activeHighlight,
			currentlySelectedThreadKey,
			getCommentIndex,
			orderedActiveAnnotations,
			footerCommentIds,
			footerCommentsLoadingState,
			handleNextCommentNavigation,
			footerCommentThreads,
			openInlineComment,
			scrollToFooterComment,
		],
	);

	const handleCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			// Define behavior when the comments panel is shown - panel FG is a prereq for nav, so has to be checked first
			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('confluence-frontend-comments-panel') &&
				isObjectSidebarShown &&
				panel?.id === PanelName.CommentsPanel
			) {
				handlePanelCommentNavigation(action);
			} else {
				// Comments panel not shown
				handleStandaloneCommentNavigation(action);
			}
		},
		[
			isObjectSidebarShown,
			panel?.id,
			handlePanelCommentNavigation,
			handleStandaloneCommentNavigation,
		],
	);

	useActiveCommentsQuery({
		pageId: contentId || '',
		skip:
			!triggeredByUnreadKeyboardShortcut ||
			alreadyFetchedInitialData ||
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		onCompletedFn: () => {
			setAlreadyFetchedInitialData(true);
		},
	});

	// The flow is setPendingStandaloneAction --> load footer comments --> updates footerCommentIds --> retrigger KB shortcut with updated data
	const shouldLoadFooterComments = pendingStandaloneAction !== null;

	useActiveCommentsQuery({
		pageId: contentId || '',
		skip:
			!shouldLoadFooterComments ||
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		fetchPolicy: shouldLoadFooterComments ? 'network-only' : 'cache-only',
		type: CommentsType.FOOTER,
	});

	useEffect(() => {
		if (pendingStandaloneAction !== null) {
			handleStandaloneCommentNavigation(pendingStandaloneAction);
			setPendingStandaloneAction(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [footerCommentIds]);

	const handleNextUnreadNavigation = useCallback(() => {
		if (!alreadyFetchedInitialData) {
			//This will trigger the ActiveComments query if it's not already fetched
			setTriggeredByUnreadKeyboardShortcut(true);
			//Exit because the comment data won't be available yet to navigate to
			return;
		}

		//unread panel navigation
		if (
			isObjectSidebarShown &&
			panel?.id === PanelName.CommentsPanel &&
			fg('confluence-frontend-comments-panel')
		) {
			let nextThreadKey = '';
			let nextCommentType = CommentType.GENERAL;
			if (!currentlySelectedThreadKey) {
				if (unreadInlineComments.length > 0) {
					nextThreadKey = unreadInlineComments[0];
					nextCommentType = CommentType.INLINE;
				} else {
					nextThreadKey = unreadGeneralComments[0];
				}
			} else {
				nextThreadKey =
					getNextUnreadThreadKey(
						getCommentIndex(currentlySelectedThreadKey ?? '') + 1, //Add one otherwise it might get stuck on the current comment because there are unread replies which are collapsed
						[...orderedActiveAnnotations, ...footerCommentIds],
						[...unreadInlineComments, ...unreadGeneralComments],
					) ?? '';
				if (unreadInlineComments.includes(nextThreadKey)) {
					nextCommentType = CommentType.INLINE;
				}
			}

			// Use unified keyboard shortcut navigation for panel navigation when feature is enabled
			if (fg('confluence_comments_unified_navigation')) {
				// Handle panel selection state management in the listener
				setCurrentlySelectedThreadKey(nextThreadKey);

				handleKeyboardShortcutNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					block: 'center',
					annotationId: nextThreadKey,
					commentType:
						nextCommentType === CommentType.GENERAL ? CommentsType.FOOTER : CommentsType.INLINE,
					isCommentsPanelOpen: isObjectSidebarShown && fg('confluence-frontend-comments-panel'),
					hasKeyPressed: true,
					context: NavigationContext.RENDERER,
					shortcutType: ShortcutType.UNREAD,
				});
			} else {
				// Use old method when unified navigation is disabled
				handlePanelSelectionAndScroll({
					threadKey: nextThreadKey,
					isResolved: false,
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: emitter,
					commentType: nextCommentType,
					scrollCommentsPanelToThread,
					shouldScrollCommentsPanel: true,
				});
			}
			return;
		}

		if (activeHighlight || currentlySelectedThreadKey) {
			handleNextCommentNavigation({
				isNavigateToNextUnread: true,
				action: 'next',
			});
		} else {
			if (unreadInlineComments.length + unreadGeneralComments.length === 0) {
				return;
			}
			if (unreadInlineComments[0]) {
				openInlineComment(unreadInlineComments[0]);
			} else {
				scrollToFooterComment(unreadGeneralComments[0]);
			}
		}
	}, [
		isObjectSidebarShown,
		panel?.id,
		handleNextCommentNavigation,
		unreadInlineComments,
		activeHighlight,
		openInlineComment,
		scrollToFooterComment,
		currentlySelectedThreadKey,
		unreadGeneralComments,
		emitter,
		footerCommentIds,
		getCommentIndex,
		orderedActiveAnnotations,
		pageMode,
		scrollCommentsPanelToThread,
		setCurrentlySelectedThreadKey,
		alreadyFetchedInitialData,
	]);

	useEffect(() => {
		if (
			unreadInlineComments.length + unreadGeneralComments.length > 0 &&
			alreadyFetchedInitialData &&
			triggeredByUnreadKeyboardShortcut
		) {
			// This handles the case where user triggers unread keyboard shortcut when the comments data isn't fetched yet.
			// We need to wait for the data to be fetched before calling handleNextUnreadNavigation
			setTriggeredByUnreadKeyboardShortcut(false);
			// Putting this behind a timeout because need to wait for triggeredByUnreadKeyboardShortcut to be set to false otherwise got called multiple times
			setTimeout(() => {
				handleNextUnreadNavigation();
			}, 100);
		}
	}, [
		unreadInlineComments,
		unreadGeneralComments,
		handleNextUnreadNavigation,
		alreadyFetchedInitialData,
		triggeredByUnreadKeyboardShortcut,
		setTriggeredByUnreadKeyboardShortcut,
	]);

	const openCommentEditorShortcutCb = (e: KeyboardEvent) => {
		e.preventDefault();
		// This check is necessary to see which keyboard shortcut was used so it can be sent to analytics.
		// e.key is used to get the key that was pressed according to the current keyboard layout.
		// We compare with CREATE_COMMENT_SHORTCUT because it is a single-letter keyboard shortcut.
		// It's more difficult to compare with UNIVERSAL_OPEN_COMMENT_EDITOR because it includes modifiers.  Modifiers which change what e.key maps to depending on the keyboard layout.
		const shortcut =
			e.key === CREATE_COMMENT_SHORTCUT ? CREATE_COMMENT_SHORTCUT : UNIVERSAL_OPEN_COMMENT_EDITOR;

		if (canAddComments || !fg('confluence_fe_add_comment_kb_check_permission')) {
			if (document.querySelector('[data-testid="highlightActionsPopup"]')) {
				const createButton = document.querySelector('#create-inline-comment-button');
				// if the disabled attribute is there, and comes back as an empty string, it is disabled.
				// If the disabled attribute is not there, it will come back as null
				const createButtonIsDisabled = createButton?.getAttribute('disabled') === '';
				// If createButton is not found or is disabled, do nothing.
				// This handles the case when hovering over media (image/video) objects in renderer.
				// In this scenario, a `highlightActionsPopup` is shown but the Comment button (which
				// has a different id) behaves differently: if any comment exists on the media object,
				// button click opens it as standalone; otherwise button click opens CreateComment.
				// Keyboard shortcut is disabled for this scenario.
				const createButtonIsEnabled = createButton && createButton?.getAttribute('disabled') !== '';

				if (
					fg('confluence_fe_create_inline_comment_exp_coverage')
						? createButtonIsEnabled
						: !createButtonIsDisabled
				) {
					handleCommentButtonClick({
						dialogs,
						shortcut,
						experienceAttributes: { entryPoint: 'keyboardShortcut' },
					});
				}
			} else {
				// Check if we should show sticky draft instead of scrolling to object comment wrapper
				const isShowStickyDraft =
					!isObjectCommentWrapperVisible &&
					(addFooterCommentAdf || expanded) &&
					footerEditorEntry !== AddFooterCommentMode.STICKY_DRAFT &&
					fg('confluence_comment_sticky_footer_enabled');

				if (!expanded) {
					expandCommentContainer({ entryPoint: 'keyboardShortcutView' });
				}

				if (
					isShowStickyDraft ||
					(!isObjectCommentWrapperVisible && fg('confluence_comment_expand_footer_editor'))
				) {
					setFooterEditorEntry(AddFooterCommentMode.STICKY_DRAFT);
				} else {
					document
						.querySelector(`[data-testid='object-comment-wrapper']`)
						?.scrollIntoView({ behavior: 'smooth' });
				}
			}
		}

		sendKeyboardShortcutAnalytics(shortcut);
	};

	if (!isFabricPage) return null;

	return (
		<div data-testid="comments-shortcut-listener">
			{fg('confluence_frontend_comments_a11y_kb') && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_SELECT_COMMENT_SHORTCUT}
					listener={() => {
						const openComment =
							document.getElementById('renderer-comments-sidebar') ||
							document.getElementById(`comment-thread-${currentlySelectedThreadKey}-container`);
						if (openComment) {
							openComment.setAttribute('tabIndex', '-1');
							openComment.focus();
						}
					}}
				/>
			)}
			<GeneralShortcutListener
				accelerator={UNIVERSAL_NEXT_COMMENT_SHORTCUT}
				deprecatedAccelerator={NEXT_COMMENT_SHORTCUT}
				listener={() => {
					selectInlineCommentCompoundExperience.selectAnnotation.start({
						pageClass,
						entryPoint: 'keyboard-navigation',
					});
					selectInlineComment.debug('keyboard navigation to next comment', {
						// whenTriggered props added to help debug on Hello (for reproducing issues)
						activeHighlightWhenTriggered: activeHighlight,
						currentlySelectedThreadKeyWhenTriggered: currentlySelectedThreadKey,
					});
					handleCommentNavigation('next');
				}}
			/>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_PREV_COMMENT_SHORTCUT}
				deprecatedAccelerator={PREV_COMMENT_SHORTCUT}
				listener={() => {
					selectInlineCommentCompoundExperience.selectAnnotation.start({
						pageClass,
						entryPoint: 'keyboard-navigation',
					});
					selectInlineComment.debug('keyboard navigation to previous comment', {
						// whenTriggered props added to help debug on Hello (for reproducing issues)
						activeHighlightWhenTriggered: activeHighlight,
						currentlySelectedThreadKeyWhenTriggered: currentlySelectedThreadKey,
					});
					handleCommentNavigation('previous');
				}}
			/>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_NEXT_UNREAD_COMMENT_SHORTCUT}
				listener={() => {
					selectInlineCommentCompoundExperience.selectAnnotation.start({
						pageClass,
						entryPoint: 'keyboard-navigation',
					});
					selectInlineComment.debug('Keyboard navigation to next unread comment', {
						// whenTriggered props added to help debug on Hello (for reproducing issues)
						activeHighlightWhenTriggered: activeHighlight,
						currentlySelectedThreadKeyWhenTriggered: currentlySelectedThreadKey,
					});
					handleNextUnreadNavigation();
				}}
			/>
			{fg('confluence_frontend_content_wrapper') && (
				<GeneralShortcutListener
					deprecatedAccelerator={CREATE_COMMENT_SHORTCUT}
					accelerator={UNIVERSAL_OPEN_COMMENT_EDITOR}
					listener={(e) => openCommentEditorShortcutCb(e)}
				/>
			)}
		</div>
	);
};

export const ViewCommentShortcutListener = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(withDialogs(ViewCommentShortcutListenerComponent));
